import { Typography } from '@mui/material';
import { useMemo } from 'react';
import { MapWithPreview } from '@/features/map';
import { useParams } from 'react-router';
import { useGetAreaQuery, useGetZonesQuery } from '@/models/location';
import { Summary } from '@/features/summary';
import { AdStats } from '@/pages/ad-details/AdStats';
import { AdTopBar } from '@/components/AdTopBar';
import { Tablet } from '@/features/upload';
import { AdStatsChart } from '@/pages/ad-details/AdStatsChart';
import { ProductType, useGetAdQuery } from '@/models/ad';
import type { Zone } from '@/models/ad/types';
import { Trans } from 'react-i18next';

export const AdDetails = () => {
  const { id } = useParams();

  const { data: ad } = useGetAdQuery({ id: id || '' }, { skip: !id });

  const { data: area } = useGetAreaQuery(
    { areaId: ad?.zones[0]?.zone.area.id || 0 },
    { skip: !ad }
  );

  const { data: zones } = useGetZonesQuery(
    { areaId: ad?.zones[0]?.zone.area.id || 0 },
    { skip: !ad }
  );

  const selectedZones = useMemo(
    () => ad?.zones?.map((it: { zone: Zone }) => it.zone.id) || [],
    [ad]
  );

  const getZones = () =>
    ad?.zones.map((it: { zone: Zone }) => it.zone.id) || [];

  return (
    <div className="xl:flex xl:flex-col w-full">
      <AdTopBar id={id} title={ad?.title} state={ad?.state} />
      <div className="xl:flex">
        <div className="flex flex-col lg:flex-row">
          <div className="w-full lg:w-3/4 mr-4">
            {ad && (
              <div className="mb-8">
                <AdStats data={ad} />
              </div>
            )}
            {ad?.payment?.amount && (
              <AdStatsChart
                totalDisplaysBought={ad?.stats.displays.total}
                totalViewsBought={ad?.stats.views.total}
                price={Number(ad?.payment.amount)}
              />
            )}
            {ad && (
              <div className="mb-8">
                <Tablet
                  mediaUrl={ad.url}
                  productType={ad.type}
                  qrUrl={ad.qrUrl}
                  qrText={ad.qrText}
                />
              </div>
            )}
            <div className="mb-8">
              <Typography variant="h6" sx={{ mb: '20px' }}>
                <Trans i18nKey="ad_details.selected_zones.title" />
              </Typography>
              {area && (
                <MapWithPreview
                  area={area}
                  selectedZones={selectedZones}
                  height="50vh"
                  zones={zones}
                />
              )}
            </div>
          </div>
          <div className="xl:sticky xl:top-20 xl:self-start xl:grow-0 xl:shrink-0 lg:basis-5/13 xl:basis-3/12">
            <Summary
              type={ad?.type || ProductType.Image}
              zones={getZones()}
              displays={ad?.stats.displays.total || 0}
              views={ad?.stats.views.total || 0}
              areaId={area?.id || 0}
              onSetValue={() => undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
